import React from 'react';
import styled from '@emotion/styled';
import { Icon, Popup, Table } from 'semantic-ui-react';
import ContinWaiting from './index';

interface WaitingSearch {
  id: number;
  searchCategoryId: number;
  searchCode?: string;
  searchTypeCode?: string;
  waitingFor?: string;
}

const getCategorySearches = (searches: WaitingSearch[], category: number) =>
  Array.from(
    new Map(
      searches
        .filter(search => search.searchCategoryId === category)
        .map(search => [search.id, search] as [number, WaitingSearch])
    ).values()
  );

const Cell = styled(Table.Cell)`
  && {
    white-space: normal;
    overflow-wrap: anywhere;
  }
  .waiting-print {
    display: none;
  }
  @media print {
    && {
      white-space: normal !important;
      overflow: visible !important;
    }
    .waiting-editor, .waiting-summary-icon {
      display: none !important;
    }
    .waiting-print {
      display: block;
    }
  }
`;

interface Props {
  searches: WaitingSearch[];
  category: number;
  onSubmit: () => Promise<void>;
}

export default function ReportWaitingCell({ searches, category, onSubmit }: Props) {
  const items = getCategorySearches(searches, category);
  if (category === 1 && items.length > 1) {
    const waitingSearches = items.filter(search => search.waitingFor?.trim());
    if (!waitingSearches.length) return <Cell />;
    const details = waitingSearches.map(search => (
      <div key={search.id}>
        {search.searchCode || search.searchTypeCode}: waiting on {search.waitingFor}
      </div>
    ));

    return (
      <Cell>
        <Popup
          on={['hover', 'focus']}
          hoverable
          position="bottom left"
          // Override the global .bottom utility so the popup fits its content.
          style={{ bottom: 'auto', maxWidth: 400 }}
          content={<div style={{ maxHeight: 360, overflowY: 'auto', overflowWrap: 'anywhere' }}>{details}</div>}
          trigger={
            <button
              type="button"
              className="waiting-summary-icon"
              aria-label="View municipal waiting reasons (read only)"
              style={{ background: 'none', border: 0, padding: 0, cursor: 'default' }}
            >
              <Icon name="hourglass half" circular color="red" />
            </button>
          }
        />
        {waitingSearches.length} / {items.length} searches waiting
        <div className="waiting-print">{details}</div>
      </Cell>
    );
  }
  if (items.length !== 1) return <Cell />;
  const search = items[0];

  return (
    <Cell>
      <span className="waiting-editor">
        <ContinWaiting
          searchId={search.id}
          waitingFor={search.waitingFor}
          onSubmit={onSubmit}
        />
      </span>
      {search.waitingFor}
    </Cell>
  );
}
